import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation
} from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, of, Subject } from 'rxjs';
import {
  combineLatestWith,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  takeUntil,
  tap
} from 'rxjs/operators';
import { getDriverName, validGpsData } from '@app/modules/shared/utilities/utilities';
import { UiUtilities } from '@app/services/ui-utilities';
import { LocationFacade } from '@app/modules/location/facade/location.facade';
import { MarkerIconService } from '@app/modules/location/services/marker-icon.service';
import { ResourceLoadState } from '@app/store/filters/models/resource-load.state';
import { ViewableAsset } from '@app/modules/location/models/viewable-asset.model';
import { UntypedFormControl } from '@angular/forms';
import { DataDogService } from '@app/services/data-dog.service';
import { DetailsSubcontext, ViewContext } from '@app/store/layout/reducers/layout.reducer';
import { SettingsApiService } from '@app/services/settings-api.service';
import { MapSettings } from '@app/modules/location/models/settings.model';
import { TranslateService } from '@zonar-ui/i18n';
import { Translations } from '@app/core/services/i18n/translations.service';
import { SortAttribute } from '@app/services/filters.model';

import { LocationApiService } from '@app/modules/location-client/location-api.service';
import { Asset, PostBodyConfig } from '@app/modules/location-client/location-api.models';
import { postBodyConfigFromFilters } from '@app/modules/location-client/utilities';
import { environment } from '@environments/environment';

import { FiltersService } from '@app/services/filters.service';

@Component({
  selector: 'app-asset-list',
  templateUrl: './asset-list.component.html',
  styleUrls: ['./asset-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class AssetListComponent implements OnInit, OnDestroy {
  @Output() hoverOut: EventEmitter<object> = new EventEmitter<object>();
  @Output() hoverOver: EventEmitter<object> = new EventEmitter<object>();

  onDestroy$ = new Subject<void>();
  assets: ViewableAsset[] = [];
  assetCount: number = 0;
  assetCountResults = new BehaviorSubject<string>(null);
  assetsLoaded = false;
  listTitle: string;

  focusedAssetId: string | null = null;

  listHasFocus = false;

  sortingControl = new UntypedFormControl();
  sortingOptions = [
    { viewValue: 'Asset (A-Z)', sortKey: SortAttribute.ASSET_NAME, sortAsc: true },
    { viewValue: 'Asset (Z-A)', sortKey: SortAttribute.ASSET_NAME, sortAsc: false },
    { viewValue: 'Driver (A-Z)', sortKey: SortAttribute.DRIVER_NAME, sortAsc: true },
    { viewValue: 'Driver (Z-A)', sortKey: SortAttribute.DRIVER_NAME, sortAsc: false },
    { viewValue: 'Newest updates', sortKey: SortAttribute.UPDATES, sortAsc: false },
    { viewValue: 'Oldest updates', sortKey: SortAttribute.UPDATES, sortAsc: true }
  ];
  sortingDefaultValue = this.sortingOptions[0];
  currentSortKey;
  currentSortAsc;

  skeletonMaxCount = 15;
  translated: any;

  // TODO: if we need to vary polling by zoom, push to this behavior subject
  pollingInterval$ = new BehaviorSubject<number>(environment.liveUpdate.pollingInterval);
  postBodyConfig$ = new BehaviorSubject<PostBodyConfig>({ companyId: null });
  pollingCanceller$ = new Subject<Boolean>();

  firstLoadForDD = true;

  // vars for scroll pagination behavior
  listUpdating = false;
  LIST_PAGESIZE = 40;
  listOffset = 0;
  listUpperOffsetLimit;
  maxAssetsFetched = 0;
  maxAttainedListOffset = -1;

  constructor(
    private locationApiService: LocationApiService,
    private locationFacade: LocationFacade,
    public markerIconService: MarkerIconService,
    public dataDog: DataDogService,
    private settingsService: SettingsApiService,
    public translations: Translations,
    public translateService: TranslateService,
    private uiUtils: UiUtilities,
    private filtersService: FiltersService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.translations.translationsLoadState
      .pipe(
        filter(loadstate => loadstate === ResourceLoadState.LOAD_SUCCESSFUL),
        mergeMap(_ => {
          return of(
            this.translateService.instant([
              this.translations.appAssetList.assetList,
              this.translations.appAssetList.results,
              this.translations.appAssetList.noResultsFound,
              this.translations.appAssetList.sortBy.assetAsc,
              this.translations.appAssetList.sortBy.assetDesc,
              this.translations.appAssetList.sortBy.driverAsc,
              this.translations.appAssetList.sortBy.driverDesc,
              this.translations.appAssetList.sortBy.oldestUpdates,
              this.translations.appAssetList.sortBy.newestUpdates
            ])
          );
        }),
        takeUntil(this.onDestroy$)
      )
      .subscribe(translations => {
        this.translated = translations;
        this.sortingOptions[0].viewValue = translations[this.translations.appAssetList.sortBy.assetAsc];
        this.sortingOptions[1].viewValue = translations[this.translations.appAssetList.sortBy.assetDesc];
        this.sortingOptions[2].viewValue = translations[this.translations.appAssetList.sortBy.driverAsc];
        this.sortingOptions[3].viewValue = translations[this.translations.appAssetList.sortBy.driverDesc];
        this.sortingOptions[4].viewValue = translations[this.translations.appAssetList.sortBy.newestUpdates];
        this.sortingOptions[5].viewValue = translations[this.translations.appAssetList.sortBy.oldestUpdates];
      });

    this.settingsService
      .getSetting(MapSettings.MAP_SORT_ORDER)
      .pipe(take(1))
      .subscribe(setting => {
        const initialSetting = this.mapSortSettingToOption(setting.value);
        this.currentSortKey = initialSetting.sortKey;
        this.currentSortAsc = initialSetting.sortAsc;
      });
    this.locationFacade.setViewContext(ViewContext.LIST);
    this.locationFacade.setViewSubContext(DetailsSubcontext.LIVE);

    this.filtersService
      .getFiltersState()
      .pipe(
        filter(filters => filters.companyId != null),
        distinctUntilChanged((prev, curr) => {
          const prevFilter = { ...prev, northEast: undefined, southWest: undefined };
          const currFilter = { ...curr, northEast: undefined, southWest: undefined };
          return JSON.stringify(prevFilter) === JSON.stringify(currFilter);
        }),
        takeUntil(this.onDestroy$)
      )
      .subscribe(filters => {
        const sortAsc = filters.sortOrder == null ? this.currentSortAsc : filters.sortOrder === 'asc';
        const sortKey = filters.sortAttribute || this.currentSortKey;
        const selectedOption = this.sortingOptions.find(o => o.sortAsc === sortAsc && o.sortKey === sortKey);
        this.sortingControl.setValue(selectedOption, { emitEvent: false });

        // reset offset when filters change
        this.listOffset = 0;
        this.maxAssetsFetched = 0;
        this.maxAttainedListOffset = -1;
        this.listUpperOffsetLimit = null;
        // clear assets and display strings
        this.assets = [];
        this.assetsLoaded = false;

        const config = postBodyConfigFromFilters(filters, {
          pageSize: this.LIST_PAGESIZE,
          sortBy: sortKey,
          sortOrder: filters.sortOrder ? filters.sortOrder : this.currentSortAsc ? 'asc' : 'desc'
        });
        // asset list does not take map bounds into account
        delete config?.northEast;
        delete config?.southWest;

        // stop scroll from changing offset while we wait for new assets
        this.listUpdating = true;

        this.postBodyConfig$.next(config);

        // for count, remove things the count request doesn't handle
        const countbody: PostBodyConfig = {
          ...config,
          sortBy: undefined,
          sortOrder: undefined,
          pageSize: undefined,
          offset: undefined
        };

        // get asset count for filters
        this.locationApiService
          .getAssetsCount(countbody)
          .pipe(take(1))
          .subscribe(res => {
            this.assetCount = res.count;
            if (this.assetCount) {
              this.setListUpperOffsetLimit();
              this.assetCountResults.next(
                `(${this.assetCount} ${this.translated[this.translations.appAssetList.results]})`
              );
            } else {
              this.listTitle = this.translated[this.translations.appAssetList.noResultsFound];
            }
            // OnPush: a count of 0 changes no async binding, so the header would keep the previous count
            this.cdr.markForCheck();
          });
      });

    // this is polling mechanism for list
    this.postBodyConfig$
      .pipe(
        filter(pbc => pbc.companyId != null),
        tap(() => {
          this.pollingCanceller$.next(true);
        }), // should cancel the prior polling subscription
        map(p => {
          // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
          this.maxAssetsFetched = Math.max(this.maxAssetsFetched, this.listOffset + p.pageSize);
          this.locationApiService
            .getAssets(p)
            .pipe(take(1))
            .subscribe(assets => {
              this.assetsLoaded = true;
              if (this.firstLoadForDD) {
                this.dataDog.newRumTiming('asset_list_loaded');
                this.firstLoadForDD = false;
              }
              this.listTitle = this.translated[this.translations.appAssetList.assetList];
              if (this.listOffset > this.maxAttainedListOffset) {
                this.maxAttainedListOffset = Math.max(this.maxAttainedListOffset, this.listOffset);
                const newAssets = (assets as Array<Asset>).map(asset => ({
                  ...asset,
                  iconUrl: this.markerIconService.fetchAssetsListIconUrl(asset),
                  subTitle: this.uiUtils.assetSubtitle(asset),
                  className: this.markerIconService.fetchAssetClassName(asset),
                  sidebarMessage: this.getSidebarMessageString(asset),
                  driverName: getDriverName(asset)
                }));
                // Existing assets are placed first to preserve pagination order (page 1 at top,
                // subsequent pages appended below). New data for any duplicate assetId wins
                // because dedupeAssets iterates left-to-right and the last write wins — but
                // since paginated pages should not share assetIds, duplicates are only a
                // safety net for boundary-shift edge cases.
                this.assets = this.dedupeAssets([...this.assets, ...newAssets]);
                this.cdr.markForCheck();
                this.listUpdating = false;
              }
            });
          return p;
        }),
        combineLatestWith(this.pollingInterval$),
        tap(() => {
          this.pollingCanceller$.next(true);
        }), // should cancel the prior polling subscription
        switchMap(([p, i]) => {
          const newP = { ...p, pageSize: this.maxAssetsFetched, offset: 0 };
          this.listTitle = this.translated[this.translations.appAssetList.assetList]; // this is sometimes hit before the initial
          // call to getAssets above.
          this.listUpdating = true;
          return this.locationApiService.polling<Asset[]>(
            () => this.locationApiService.getAssets(newP),
            i,
            this.pollingCanceller$
          );
        }),
        takeUntil(this.onDestroy$)
      )
      .subscribe((assets: any) => {
        this.assets = (assets as Array<Asset>).map(asset => ({
          ...asset,
          iconUrl: this.markerIconService.fetchAssetsListIconUrl(asset),
          subTitle: this.uiUtils.assetSubtitle(asset),
          className: this.markerIconService.fetchAssetClassName(asset),
          sidebarMessage: this.getSidebarMessageString(asset),
          driverName: getDriverName(asset)
        }));
        this.assetsLoaded = true;
        this.listUpdating = false;
        this.cdr.markForCheck();
      });

    // updates sorting in filter state (which informs postbody) and persistent settings
    this.sortingControl.valueChanges.subscribe(({ sortKey, sortAsc }) => {
      const sortOrder = sortAsc ? 'asc' : 'desc';
      this.filtersService.setSortingState(sortKey, sortOrder);
      this.saveSortSetting(sortKey, sortAsc);
    });
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  onScroll() {
    if (this.listOffset < this.listUpperOffsetLimit && !this.listUpdating) {
      this.listOffset += this.LIST_PAGESIZE;
      this.postBodyConfig$.pipe(take(1)).subscribe(pbc => {
        this.listUpdating = true;
        this.postBodyConfig$.next({ ...pbc, offset: this.listOffset });
      });
    }
  }

  // determines how many results will be on the last page from the API and sets an offset limit,
  // to stop us from making bad requests when we scroll too far down
  setListUpperOffsetLimit() {
    const lastPageCount = this.assetCount % this.LIST_PAGESIZE;
    if (this.assetCount < this.LIST_PAGESIZE) {
      // if there are less assets than one page, don't ever increase offset
      this.listUpperOffsetLimit = 0;
    } else if (lastPageCount > 0) {
      // otherwise the last page will be at least the size of the offset interval
      this.listUpperOffsetLimit = this.assetCount - lastPageCount;
    } else {
      // mod 0 - pages divide evenly into max count
      this.listUpperOffsetLimit = this.assetCount - this.LIST_PAGESIZE;
    }
  }

  trackFn(index, value) {
    // Track by assetId only so that a poll updating geoEventTs does not cause Angular
    // to destroy and recreate DOM rows, which would collapse the scroll position.
    return value.assetId;
  }

  getSidebarMessageString(asset: ViewableAsset): string {
    if (validGpsData(asset)) {
      return this.uiUtils.getTimeAgoString(asset.geoEventTs, false);
    }
    return '';
  }

  mapSortParamsToSetting(sortKey, sortAsc) {
    switch (sortKey) {
      case SortAttribute.ASSET_NAME:
        return sortAsc ? 'ASSET_ASC' : 'ASSET_DESC';
      case SortAttribute.DRIVER_NAME:
        return sortAsc ? 'DRIVER_ASC' : 'DRIVER_DESC';
      case SortAttribute.UPDATES:
        return sortAsc ? 'OLDEST' : 'NEWEST';
    }
  }

  mapSortSettingToOption(settingValue: string) {
    switch (settingValue) {
      case 'ASSET_ASC':
        return this.sortingOptions[0];
      case 'ASSET_DESC':
        return this.sortingOptions[1];
      case 'DRIVER_ASC':
        return this.sortingOptions[2];
      case 'DRIVER_DESC':
        return this.sortingOptions[3];
      case 'NEWEST':
        return this.sortingOptions[4];
      case 'OLDEST':
        return this.sortingOptions[5];
      default:
        return this.sortingDefaultValue;
    }
  }

  saveSortSetting(sortKey: SortAttribute, sortAsc: boolean): void {
    if (sortKey !== this.currentSortKey || sortAsc !== this.currentSortAsc) {
      const settingChoice = this.mapSortParamsToSetting(sortKey, sortAsc);
      this.currentSortKey = sortKey;
      this.currentSortAsc = sortAsc;
      this.settingsService.saveSetting(MapSettings.MAP_SORT_ORDER, settingChoice).pipe(take(1)).subscribe();
    }
  }

  protected readonly event = event;

  dedupeAssets(assets: Asset[]): Asset[] {
    const uniqueAssets: Record<string, Asset> = {};

    assets.forEach(asset => {
      uniqueAssets[asset.assetId] = asset;
    });

    return Object.values(uniqueAssets);
  }

  /**
   * When the listbox receives focus, mark it as focused for styling purposes.
   * Does not auto-scroll to index 0 — doing so would reset scroll position when
   * the user returns to the list after viewing asset details, since focusedAssetId
   * is only set by keyboard navigation and is null on mouse-click flows.
   * Keyboard navigation (ArrowDown/Up) is used to move through the list explicitly.
   */
  onListFocus(): void {
    this.listHasFocus = true;
  }

  /**
   * When the listbox loses focus, hide the visual highlight while retaining
   * the cursor position so re-entering the list resumes from the same item.
   */
  onListBlur(): void {
    this.listHasFocus = false;
  }

  /**
   * Handles keyboard navigation within the asset list.
   * Arrow keys move between assets, Enter/Space activates the focused asset.
   */
  onListKeydown(event: KeyboardEvent): void {
    if (this.assets.length === 0) {
      return;
    }

    const currentIndex = this.focusedAssetId ? this.assets.findIndex(a => a.assetId === this.focusedAssetId) : -1;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        // Trigger loading more assets if at end of loaded list
        if (currentIndex >= this.assets.length - 1) {
          this.onScroll();
        }
        this.focusAssetAtIndex(currentIndex + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusAssetAtIndex(currentIndex - 1);
        break;
      case 'Home':
        event.preventDefault();
        this.focusAssetAtIndex(0);
        break;
      case 'End':
        event.preventDefault();
        this.focusAssetAtIndex(this.assets.length - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (this.focusedAssetId) {
          this.navigateToAsset(this.focusedAssetId);
        }
        break;
    }
  }

  /**
   * Focuses the asset at the given index and scrolls it into view.
   */
  private focusAssetAtIndex(index: number): void {
    const clampedIndex = Math.max(0, Math.min(index, this.assets.length - 1));
    const asset = this.assets[clampedIndex];

    if (asset) {
      this.focusedAssetId = asset.assetId;
      this.hoverOver.emit(asset);

      const element = document.getElementById('asset-item-' + asset.assetId);
      if (element) {
        element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }

  /**
   * Navigates to the asset detail page.
   */
  navigateToAsset(assetId: string): void {
    this.router.navigate(['/assets', assetId, 'live']);
  }
}
